import React, { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";
import {
  useGetHrAndBillingContactsMutation,
  useGetStaffListByJobRoleOrClientId,
} from "../../../../api/services/client/clientApis";
import {
  useGetLineOfBusiness,
  useGetProgramTemplateMutation,
} from "../../../../api/services/programTemplate/programTemplateApis";
import { useGetUsersDetailByRoleMutation } from "../../../../api/services/user/userApi";
import {
  AccordionHeader,
  AccordionHeaderText,
  MuiBox,
  MuiDoubleLabel,
  MuiGrid,
  MuiTypography,
  MuiViewMorePopOver,
} from "../../../../components";
import { BillingTypeConfigureTable } from "../../../../components/billingTypeConfigureTable/BillingTypeConfigureTable";
import { BillingConfigurationDetailsFormKeyProps } from "../../../../components/billingTypeConfigureTable/modelsAndSchemas/Models";
import { MuiDoubleLabelWithChip } from "../../../../components/doubleLabelWithChip/MuiDoubleLabelWithChip";
import {
  CURRENCY_FORMAT_MAP,
  CurrencyFormat,
} from "../../../../components/textFieldCurrency/CurrencyUtils";
import {
  EMPTY_VALUE_DASH,
  gridSizeLongLabel,
  LANGUAGE_CODE,
  PAGE_IDS,
  ROLE_IDS,
  TIMEOUT_100,
} from "../../../../helpers/constants";
import {
  getContractIdName,
  getCurrencyNameAndLocaleById,
  getFormattedAmount,
  getFullNameById,
  getProgramTemplateById,
  getValueOrBlank,
  getValueOrBlankArray,
  getValueOrDash,
  isActivityBasedBilling,
  isEmpty,
  isMilestoneBilling,
  isScheduledBasedBilling,
  replaceHashWithValue,
} from "../../../../helpers/utils";
import { useForceUpdate } from "../../../../hooks/useForceUpdate";
import { useGridContainer } from "../../../../hooks/useGridContainer";
import { useMediaQueries } from "../../../../hooks/useMediaQueries";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { currencyListSelector } from "../../../../redux/slices/appState/appStateSelector";
import { getBillingConfigurationDetailsFormKeys } from "../../../createClient/helper";
import {
  BillingConfigurationItem,
  Currency,
  CreateClientFormKey as ProgramTemplateFormKey,
} from "../../../createClient/modelsAndSchemas/Models";
import { billingAndPurchaseOrderKeys } from "../../../createClient/siteCore/SiteCoreKeys";
import { getPreferencesChipsFromBillingConfigs } from "../../utils";
import { ViewBillingConfigurationsWrapper } from "./ViewBillingConfigurations.style";

interface ViewBillingConfigurationsProps {
  clientId: string;
  data: BillingConfigurationItem[];
}

const ViewBillingConfigurations: React.FC<ViewBillingConfigurationsProps> = ({
  data,
  clientId,
}) => {
  const { setValue } = useFormContext();
  const { fetchLabel, fetchValueByKeyOfDropdown, commonLabels } = useSiteCore();
  const { isMobile } = useMediaQueries();
  const { commonARIALabel } = useNavigationSiteCore();
  const { billingDetailsAndPOTitle } = billingAndPurchaseOrderKeys;
  const { gridSizeMedium: gridSize } = useGridContainer();

  const { data: lineOfBusinessData, isLoading: businessPending } =
    useGetLineOfBusiness(LANGUAGE_CODE.EN_US);

  const pageId = PAGE_IDS.VIEW_BILLING_CONFIGURATION;

  const { mutateAsync: getHrContacts, data: hrContacts } =
    useGetHrAndBillingContactsMutation();

  const { mutateAsync: getBillingContacts, data: billingContacts } =
    useGetHrAndBillingContactsMutation();

  const { data: staffListData } = useGetStaffListByJobRoleOrClientId({
    clientId,
  });

  const { mutateAsync: getProjectManagersByRole, data: projectManagersList } =
    useGetUsersDetailByRoleMutation();

  const {
    mutateAsync: getProjectCoOrdinatorsByRole,
    data: projectCoOrdinatorsList,
  } = useGetUsersDetailByRoleMutation();

  const {
    mutateAsync: getProgramTemplateDropdownData,
    data: programTemplateList,
  } = useGetProgramTemplateMutation();

  const [isDataScheduleAdded, setIsDataScheduleAdded] =
    useState<boolean>(false);

  const [
    billingConfigurationDetailsFormKeysList,
    setBillingConfigurationDetailsFormKeysList,
  ] = useState<BillingConfigurationDetailsFormKeyProps[][]>(
    [] as BillingConfigurationDetailsFormKeyProps[][],
  );

  const forceUpdate = useForceUpdate();

  useEffect(() => {
    if (isEmpty(clientId)) return;
    getHrContacts({ clientId, contactType: "hr" });
    getBillingContacts({ clientId, contactType: "billing" });
  }, [clientId]);

  useEffect(() => {
    getProjectManagersByRole({ roleIds: [ROLE_IDS.projectManager] });
    getProjectCoOrdinatorsByRole({ roleIds: [ROLE_IDS.projectCoOrdinator] });
    getProgramTemplateDropdownData();
  }, []);

  const getLabel = (key: keyof typeof billingAndPurchaseOrderKeys) => {
    return fetchLabel(
      billingDetailsAndPOTitle,
      billingAndPurchaseOrderKeys[key],
    );
  };
  const getDropdownValues = (
    key: keyof typeof billingAndPurchaseOrderKeys,
    value: string,
  ) => {
    if (isEmpty(value)) return "";
    return fetchValueByKeyOfDropdown(
      billingDetailsAndPOTitle,
      billingAndPurchaseOrderKeys[key],
      value,
    );
  };

  const getLineOfBusinessName = (id: string) => {
    if (businessPending || !lineOfBusinessData) return "";
    const business = lineOfBusinessData.find(
      (item: { id: string; name: string }) => item.id === id,
    );
    return business ? business.name : "";
  };

  const currencyList = useSelector(currencyListSelector);

  const getFormattedValue = (
    value: number | undefined,
    feeCurrency: string,
  ): string => {
    const currencyData = getCurrencyNameAndLocaleById(
      currencyList,
      feeCurrency,
    );

    const formattedAmount = value
      ? `${feeCurrency} ${getFormattedAmount(value, currencyData?.currencyLocale)}`
      : EMPTY_VALUE_DASH;
    return formattedAmount;
  };

  const employeeBandCheckboxLabel = getLabel("employeeBandCheckbox");
  const employeeIDCheckboxLabel = getLabel("employeeIDCheckbox");

  const shouldShowScheduleTable = (billingTypeId: string) => {
    return (
      billingTypeId &&
      (isScheduledBasedBilling(billingTypeId) ||
        isMilestoneBilling(billingTypeId))
    );
  };

  const getCurrencyName = (currency?: Currency) => {
    return getValueOrBlank(currency?.name);
  };

  const getCurrencyFormat = (currency?: Currency) => {
    const currencyFormat: CurrencyFormat =
      CURRENCY_FORMAT_MAP[getCurrencyName(currency)];
    return currencyFormat ?? "";
  };

  useEffect(() => {
    if (!data) return;
    const billingConfigurationDetailsFormKeysListTemp: BillingConfigurationDetailsFormKeyProps[][] =
      [];
    data.forEach((item, configIndex) => {
      const programTemplateConfigList: BillingConfigurationDetailsFormKeyProps[] =
        [];
      item.programTemplateConfigurations.forEach((template, templateIndex) => {
        const formIdPrefix = `${ProgramTemplateFormKey.configurations}.${configIndex}.${ProgramTemplateFormKey.programTemplateConfigurations}.${templateIndex}`;

        const billingConfigurationDetailsFormKeys: BillingConfigurationDetailsFormKeyProps =
          getBillingConfigurationDetailsFormKeys(formIdPrefix);

        setValue(
          billingConfigurationDetailsFormKeys.billingTypeIdKey,
          template.billingTypeId,
        );
        setValue(
          billingConfigurationDetailsFormKeys.billingSchedulesKey,
          template.billingSchedules,
        );
        setValue(billingConfigurationDetailsFormKeys.feeKey, template?.fee);

        setValue(
          billingConfigurationDetailsFormKeys.feePaymentTypeKey,
          template?.feePaymentType,
        );
        setValue(
          billingConfigurationDetailsFormKeys.billingSchedulesKey,
          template?.billingSchedules,
        );
        setValue(
          billingConfigurationDetailsFormKeys.schedulePeriodKey,
          template?.schedulePeriod,
        );

        programTemplateConfigList.push(billingConfigurationDetailsFormKeys);
      });
      billingConfigurationDetailsFormKeysListTemp.push(
        programTemplateConfigList,
      );
    });
    setBillingConfigurationDetailsFormKeysList(
      billingConfigurationDetailsFormKeysListTemp,
    );

    setTimeout(() => {
      setIsDataScheduleAdded(true);
    }, TIMEOUT_100);
  }, [data]);

  return (
    <ViewBillingConfigurationsWrapper>
      <MuiBox>
        <MuiTypography variant="h2Bold">
          {getLabel("billingConfigurationsHeading")}
        </MuiTypography>

        {data.map((item, configIndex) => {
          return (
            <MuiBox
              key={`billing-config-${item.id}`}
              className="billing-configurations-container"
            >
              <AccordionHeader
                title={
                  <AccordionHeaderText label={item.billingConfigurationName} />
                }
                onChange={() => {}}
                isExpanded={true}
              >
                <MuiGrid
                  container
                  columnSpacing={2}
                  rowSpacing={isMobile ? 2 : 5}
                  className="billing-configurations"
                >
                  <MuiGrid size={gridSize}>
                    <MuiDoubleLabel
                      label={getLabel("billingConfigurationName")}
                      isValueWithEllipsis
                      replaceEmptyWithDash
                      value={item.billingConfigurationName}
                    />
                  </MuiGrid>
                  <MuiGrid size={gridSize}>
                    <MuiDoubleLabel
                      label={getLabel("lineOfBusiness")}
                      isValueWithEllipsis
                      replaceEmptyWithDash
                      value={getLineOfBusinessName(item.lineOfBusinessId)}
                    />
                  </MuiGrid>
                  <MuiGrid size={gridSize}>
                    <MuiDoubleLabel
                      label={getLabel("contractIdAndName")}
                      isValueWithEllipsis
                      value={getContractIdName(item.contractDocument)}
                    />
                  </MuiGrid>
                  <MuiGrid size={gridSize}>
                    <MuiDoubleLabel
                      label={getLabel("opportunityId")}
                      isValueWithEllipsis
                      value={getValueOrDash(item.opportunityId)}
                    />
                  </MuiGrid>
                  <MuiGrid size={gridSize}>
                    <MuiDoubleLabel
                      label={getLabel("opportunityName")}
                      isValueWithEllipsis
                      value={getValueOrDash(item.opportunityName)}
                    />
                  </MuiGrid>
                  <MuiGrid size={gridSize}>
                    <MuiDoubleLabel
                      label={getLabel("paymentTermsDropDown")}
                      isValueWithEllipsis
                      value={getValueOrDash(item.paymentTerms?.name)}
                    />
                  </MuiGrid>
                  <MuiGrid size={gridSize}>
                    <MuiDoubleLabel
                      label={getLabel("billingContactName")}
                      isValueWithEllipsis
                      value={getFullNameById(
                        getValueOrBlank(item.billingContactId),
                        billingContacts,
                      )}
                    />
                  </MuiGrid>
                  <MuiGrid size={gridSize}>
                    <MuiDoubleLabel
                      label={getLabel("hrContact")}
                      isValueWithEllipsis
                      value={getFullNameById(
                        getValueOrBlank(item.hrContactId),
                        hrContacts,
                      )}
                    />
                  </MuiGrid>
                  <MuiGrid size={gridSize}>
                    <MuiDoubleLabel
                      label={getLabel("additionalContactForInvoice")}
                      isValueWithEllipsis
                      value={getFullNameById(
                        getValueOrBlank(item.additionalInvoiceContactId),
                        billingContacts,
                      )}
                    />
                  </MuiGrid>
                  <MuiGrid size={gridSize}>
                    <MuiDoubleLabel
                      label={getLabel("outreachEligibility")}
                      replaceEmptyWithDash
                      value={getDropdownValues(
                        "outreachEligibilityDropdown",
                        item.outreachEligibilityId,
                      )}
                    />
                  </MuiGrid>
                  <MuiGrid size={gridSize}>
                    <MuiDoubleLabel
                      label={getLabel("poNumber")}
                      isValueWithEllipsis
                      replaceEmptyWithDash
                      value={item.poNumber}
                    />
                  </MuiGrid>

                  <MuiGrid size={gridSize}>
                    <MuiDoubleLabel
                      label={getLabel("costCentre")}
                      isValueWithEllipsis
                      replaceEmptyWithDash
                      value={item.costCentre}
                    />
                  </MuiGrid>
                  <MuiGrid size={gridSize}>
                    <MuiDoubleLabel
                      label={getLabel("businessUnit")}
                      isValueWithEllipsis
                      replaceEmptyWithDash
                      value={item.businessUnit}
                    />
                  </MuiGrid>

                  <MuiGrid size={gridSize}>
                    <MuiDoubleLabel
                      label={getLabel("site")}
                      isValueWithEllipsis
                      replaceEmptyWithDash
                      value={item.site}
                    />
                  </MuiGrid>
                  <MuiGrid size={gridSize}>
                    <MuiDoubleLabel
                      label={getLabel("candidateLocation")}
                      isValueWithEllipsis
                      replaceEmptyWithDash
                      value={item.candidateLocation}
                    />
                  </MuiGrid>
                  <MuiGrid size={gridSize}>
                    <MuiDoubleLabel
                      label={getLabel("salesOffice")}
                      isValueWithEllipsis
                      replaceEmptyWithDash
                      value={getValueOrBlank(item?.salesOffice?.name)}
                    />
                  </MuiGrid>
                  <MuiGrid size={gridSize}>
                    <MuiDoubleLabel
                      label={getLabel("projectManager")}
                      isValueWithEllipsis
                      value={getFullNameById(
                        getValueOrBlank(item.projectManagerId),
                        getValueOrBlankArray(projectManagersList),
                      )}
                    />
                  </MuiGrid>
                  <MuiGrid size={gridSize}>
                    <MuiDoubleLabel
                      label={getLabel("projectAdminCoordinator")}
                      isValueWithEllipsis
                      value={getFullNameById(
                        getValueOrBlank(item.projectCoordinatorId),
                        getValueOrBlankArray(projectCoOrdinatorsList),
                      )}
                    />
                  </MuiGrid>
                  <MuiGrid size={gridSize}>
                    <MuiDoubleLabel
                      label={getLabel("additionalProjectResource")}
                      isValueWithEllipsis
                      value={getFullNameById(
                        getValueOrBlank(item.additionalProjectResourceId),
                        getValueOrBlankArray(staffListData),
                      )}
                    />
                  </MuiGrid>

                  <MuiGrid size={gridSize}>
                    <MuiDoubleLabel
                      label={getLabel("clientInformation1")}
                      isValueWithEllipsis
                      replaceEmptyWithDash
                      value={item.clientInformation1}
                    />
                  </MuiGrid>
                  <MuiGrid size={gridSize}>
                    <MuiDoubleLabel
                      label={getLabel("clientInformation2")}
                      isValueWithEllipsis
                      replaceEmptyWithDash
                      value={item.clientInformation2}
                    />
                  </MuiGrid>
                  <MuiGrid size={gridSize}>
                    <MuiDoubleLabel
                      label={getLabel("clientInformation3")}
                      isValueWithEllipsis
                      replaceEmptyWithDash
                      value={item.clientInformation3}
                    />
                  </MuiGrid>

                  <MuiGrid className="select-preference-container">
                    <MuiBox>
                      <MuiDoubleLabelWithChip
                        key={item.id}
                        labelTypoVariant="h3"
                        replaceEmptyWithDash
                        valueTypoVariant="h5"
                        id="selected-preferences-chip"
                        value={getPreferencesChipsFromBillingConfigs(
                          item,
                          employeeBandCheckboxLabel,
                          employeeIDCheckboxLabel,
                        )}
                        label={getLabel("selectedPreferencesHeader")}
                        closePopoverARIALabel={commonARIALabel.closePopover}
                        showMoreARIALabel={commonARIALabel.showNMore}
                        removeChipNameARIALabel={commonARIALabel.removeChipName}
                      />
                    </MuiBox>
                  </MuiGrid>
                </MuiGrid>

                {item.programTemplateConfigurations.map(
                  (template, templateIndex) => {
                    return (
                      <MuiBox
                        key={`program-template-${template.programTemplateId}`}
                        className="program-template-container"
                      >
                        <MuiBox className="separator-space" />
                        <MuiBox className="program-template-heading">
                          <MuiTypography variant="h2Bold">
                            {replaceHashWithValue(
                              getLabel("programTemplateNumber"),
                              String(templateIndex + 1),
                            )}
                          </MuiTypography>
                        </MuiBox>

                        <MuiGrid
                          container
                          columnSpacing={2}
                          rowSpacing={isMobile ? 2 : 5}
                        >
                          <MuiGrid size={gridSize}>
                            <MuiDoubleLabel
                              label={getLabel("programTemplate")}
                              isValueWithEllipsis
                              value={getProgramTemplateById(
                                template.programTemplateId,
                                programTemplateList,
                              )}
                            />
                          </MuiGrid>
                          <MuiGrid size={gridSize}>
                            <MuiDoubleLabel
                              label={getLabel("alternateDisplayName")}
                              isValueWithEllipsis
                              replaceEmptyWithDash
                              value={template.alternateDisplayName}
                            />
                          </MuiGrid>
                          <MuiGrid size={gridSize}>
                            <MuiDoubleLabel
                              label={getLabel("billingType")}
                              replaceEmptyWithDash
                              value={getDropdownValues(
                                "billingTypeDropdown",
                                template.billingTypeId,
                              )}
                            />
                          </MuiGrid>
                          {isActivityBasedBilling(template.billingTypeId) && (
                            <MuiGrid size={gridSize}>
                              <MuiDoubleLabel
                                label={getLabel("billingTrigger")}
                                replaceEmptyWithDash
                                value={getDropdownValues(
                                  "billingTriggerDropdown",
                                  template.billingTriggerId,
                                )}
                              />
                            </MuiGrid>
                          )}
                          <MuiGrid size={gridSize}>
                            <MuiDoubleLabel
                              label={getLabel("fee")}
                              replaceEmptyWithDash
                              value={getFormattedValue(
                                Number(template.fee),
                                template?.currency?.name ?? "",
                              )}
                            />
                          </MuiGrid>
                        </MuiGrid>
                        <MuiGrid
                          container
                          className="special-billing-instructions"
                        >
                          <MuiGrid size={gridSizeLongLabel}>
                            <MuiDoubleLabel
                              label={getLabel("specialBillingInstructions")}
                              replaceEmptyWithDash
                              value={
                                template.specialBillingInstructions ? (
                                  <MuiViewMorePopOver
                                    anchorText={commonLabels.viewMore}
                                    message={
                                      template.specialBillingInstructions
                                    }
                                    placement="bottom-end"
                                  />
                                ) : (
                                  ""
                                )
                              }
                            />
                          </MuiGrid>
                        </MuiGrid>
                        {shouldShowScheduleTable(template.billingTypeId) &&
                          isDataScheduleAdded && (
                            <BillingTypeConfigureTable
                              pageId={pageId}
                              data-testid="billing-config-table"
                              currencyName={getCurrencyName(template.currency)}
                              currencyFormat={getCurrencyFormat(
                                template.currency,
                              )}
                              currencyId={getValueOrBlank(
                                template.currency?.id,
                              )}
                              forceUpdate={forceUpdate}
                              programTemplateId={getValueOrBlank(
                                template?.programTemplateId,
                              )}
                              isFromBillingInvoice={false}
                              billingConfigurationDetailsFormKeys={
                                billingConfigurationDetailsFormKeysList[
                                  configIndex
                                ][templateIndex]
                              }
                              clientId={clientId}
                              isViewOnly={true}
                            />
                          )}
                      </MuiBox>
                    );
                  },
                )}
              </AccordionHeader>
            </MuiBox>
          );
        })}
      </MuiBox>
    </ViewBillingConfigurationsWrapper>
  );
};

export default ViewBillingConfigurations;
